import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { IRoom } from "./room.types";

export const FETCH_ROOMS_LIST_PROGRESS = "FETCH_ROOMS_LIST_PROGRESS";
export const FETCH_ROOMS_LIST_SUCCESS = "FETCH_ROOMS_LIST_SUCCESS";
export const FETCH_ROOMS_LIST_FAILED = "FETCH_ROOMS_LIST_FAILED";

export const fetchRoomsListProgress = () =>
  action(FETCH_ROOMS_LIST_PROGRESS);
export const fetchRoomsListSuccess = (
  data: IRoom[],
  totalRecords: number
) => action(FETCH_ROOMS_LIST_SUCCESS, { data, totalRecords });
export const fetchRoomsListFailed = () =>
  action(FETCH_ROOMS_LIST_FAILED);

export const fetchRoomListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number = 1,
    itemPerPage: number = 100,
    pageLimit: number = 100,
    status?: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
    roomUuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchRoomsListProgress());
    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (searchByName) {
        searchQuery += `&columns=room_name&value=${searchByName}`;
      }

      if (roomUuid) {
        searchQuery += `&room_uuid=${roomUuid}`;
      }

      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date?.fromDate && date?.toDate) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const response = await api.get(
        `/leads/get-rooms?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery
      );
      const data: IRoom[] = response.data.data || [];
      const totalRecords = response.data.totalRecords || 0;
      dispatch(fetchRoomsListSuccess(data, totalRecords));
    } catch (error: any) {
      dispatch(fetchRoomsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to fetch rooms",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertRoomAsync =
  (
    data: Partial<IRoom>,
    onCallback?: (isSuccess: boolean, data?: IRoom) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const payload = {
        room_name: data.room_name,
        room_price: Number(data.room_price) || 0,
        status: data.status || "ACTIVE",
        room_uuid: data.room_uuid || null,
        company_outlet_uuid: data.company_outlet_uuid,
      };
      const response = await api.post("/leads/upsert-rooms", payload);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: data.room_uuid
            ? "Room updated successfully"
            : "Room created successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data.data);
      return response.data;
    } catch (error: any) {
      onCallback?.(false);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message || "Failed to save room",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_ROOMS_STATE = "CLEAR_ROOMS_STATE";
export const clearRoomsState = () => action(CLEAR_ROOMS_STATE);
